import React, { useState } from "react";
import {
  Show,
  SimpleShowLayout,
  TextField,
  NumberField,
  BooleanField,
  DateField,
  TopToolbar,
  EditButton,
  useNotify,
  useRefresh,
  useUpdate,
  useRecordContext,
} from "react-admin";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Switch,
  FormControlLabel,
  Box,
  Chip,
  Divider,
  Grid,
} from "@mui/material";
import {
  LocationOn,
  Phone,
  AccessTime,
  Star,
  Business,
  Public,
} from "@mui/icons-material";
import { formatNumber } from "../../../utils/formatters";

// Property Details Card Component
const PropertyDetailsCard = () => {
  const record = useRecordContext();
  
  if (!record) return null;

  return (
    <Card>
      <CardHeader>
        <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Business />
          Property Details
        </Typography>
      </CardHeader>
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Property ID
            </Typography>
            <Typography variant="body1">{record.id}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              HID
            </Typography>
            <Typography variant="body1">{record.hid}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Name
            </Typography>
            <Typography variant="body1" fontWeight="medium">{record.name}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Kind
            </Typography>
            <Chip label={record.kind} color="primary" variant="outlined" />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Star Rating
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Star sx={{ color: 'gold', fontSize: 20 }} />
              <Typography variant="body1">{record.starRating || "N/A"}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Provider
            </Typography>
            <Chip label={record.provider} variant="outlined" />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <LocationOn sx={{ color: 'text.secondary', mt: 0.5 }} />
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Address
              </Typography>
              <Typography variant="body1">{record.address}</Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Public sx={{ color: 'text.secondary' }} />
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Region
              </Typography>
              <Typography variant="body1">{record.regionName} ({record.regionType})</Typography>
            </Box>
          </Box>

          {record.phone && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Phone sx={{ color: 'text.secondary' }} />
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Phone
                </Typography>
                <Typography variant="body1">{record.phone}</Typography>
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTime sx={{ color: 'text.secondary' }} />
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Check-in/out Times
              </Typography>
              <Typography variant="body1">
                Check-in: {record.checkInTime} | Check-out: {record.checkOutTime}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Coordinates
            </Typography>
            <Typography variant="body1">Lat: {record.lat}, Long: {record.long}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Postal Code
            </Typography>
            <Typography variant="body1">{record.postalCode}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Created
            </Typography>
            <Typography variant="body1">{new Date(record.createdAt).toLocaleString()}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Updated
            </Typography>
            <Typography variant="body1">{new Date(record.updatedAt).toLocaleString()}</Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// Admin Metadata Card Component
const AdminMetadataCard = () => {
  const record = useRecordContext();
  
  if (!record?.adminMetadata) return null;

  const metadata = record.adminMetadata;

  return (
    <Card>
      <CardHeader>
        <Typography variant="h6">Admin Metadata</Typography>
      </CardHeader>
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Total Bookings
            </Typography>
            <Typography variant="body1" fontWeight="medium">{formatNumber(metadata.totalBookings)}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Last Booking Date
            </Typography>
            <Typography variant="body1">
              {metadata.lastBookingDate 
                ? new Date(metadata.lastBookingDate).toLocaleString()
                : "No bookings yet"
              }
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Sync Status
            </Typography>
            <Chip 
              label={metadata.syncStatus}
              color={metadata.syncStatus === "synced" ? "success" : "error"}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Last Sync Date
            </Typography>
            <Typography variant="body1">
              {metadata.lastSyncDate 
                ? new Date(metadata.lastSyncDate).toLocaleString()
                : "N/A"
              }
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// Visibility Update Dialog Component
const VisibilityUpdateDialog = () => {
  const record = useRecordContext();
  const [open, setOpen] = useState(false);
  const [deleted, setDeleted] = useState(record?.deleted || false);
  const [isClosed, setIsClosed] = useState(record?.isClosed || false);
  const [update, { loading }] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();

  const handleSubmit = async () => {
    try {
      await update("properties", {
        id: record.id,
        data: {
          visibility: {
            deleted,
            closed: isClosed,
          },
        },
      });
      
      notify("Property visibility updated successfully", { type: "success" });
      setOpen(false);
      refresh();
    } catch (error) {
      notify("Failed to update property visibility", { type: "error" });
    }
  };

  return (
    <>
      <Button variant="outlined" onClick={() => setOpen(true)}>
        Update Visibility
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Property Visibility</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={deleted}
                  onChange={(e) => setDeleted(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Deleted</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Hide property from search results
                  </Typography>
                </Box>
              }
            />
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isClosed}
                    onChange={(e) => setIsClosed(e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">Closed</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Mark as temporarily unavailable
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} variant="contained">
            {loading ? "Updating..." : "Update Visibility"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Status Card Component
const StatusCard = () => {
  const record = useRecordContext();
  
  if (!record) return null;

  return (
    <Card>
      <CardHeader>
        <Typography variant="h6">Status</Typography>
      </CardHeader>
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body1">Deleted</Typography>
            <Chip 
              label={record.deleted ? "Yes" : "No"}
              color={record.deleted ? "error" : "success"}
              variant="outlined"
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body1">Closed</Typography>
            <Chip 
              label={record.isClosed ? "Yes" : "No"}
              color={record.isClosed ? "error" : "success"}
              variant="outlined"
            />
          </Box>
          <Divider />
          <VisibilityUpdateDialog />
        </Box>
      </CardContent>
    </Card>
  );
};

// Custom Show Actions
const ShowActions = () => (
  <TopToolbar>
    <EditButton />
  </TopToolbar>
);

// Main Property Show Component
export const PropertyShow = () => (
  <Show actions={<ShowActions />} title="Property Details">
    <SimpleShowLayout>
      <PropertyDetailsCard />
      <AdminMetadataCard />
      <StatusCard />
    </SimpleShowLayout>
  </Show>
);
